import React, { useEffect, useState } from "react";
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Button, 
    useTheme, 
    useMediaQuery,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Skeleton
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../../assests/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import QueueManager from "../../../utils/QueueManager";
import Cookies from "js-cookie";
import { getUserFrontendUrl, getFrontendUrl, getStorageUrl } from "../../../config/config";
import { getMenus, getServiceSettings, getBranchDisplayInfo, getBranchInfoByCode } from "../../../api/api";

// Extra content box shown under the thank-you card for specific branches.
// `branchMatch` is a lowercase substring tested against the branch name.
// Generic on purpose: sub header + header (green, below it) + named link,
// usable for surveys, promos or anything a branch wants to add here.
// `linkText` takes a string, or an array to render the link over several lines.
const FEEDBACK_BOXES = [
    {
        // Majorstuen only: the survey is theirs, the other Fargerike branches don't get it.
        branchMatch: "fargerike majorstuen",
        header: "LITT FEEDBACK",
        subHeader: "Hvordan var besøket?",
        linkText: ["Kundeundersøkelse", "Fargerike Majorstuen"],
        linkUrl: "https://survey.easyquest.eu/s/RGUB1?s=easyque",
    },
];

const ThankYou = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [thankYouMsg, setThankYouMsg] = useState(null);
    const [thankYouDesc, setThankYouDesc] = useState(null);
    const [thankYouServicesLabel, setThankYouServicesLabel] = useState(null);
    // ticketData is unreliable here: some navigation paths pass only a branch id,
    // and the SMS path passes no ticketData at all. The branch is therefore resolved
    // from the API — by id when available, otherwise by the ticket code (which is the
    // branch's own code) — so branding and the feedback box work on every path.
    const [branchName, setBranchName] = useState("");
    const [resolvedBranchId, setResolvedBranchId] = useState(null);
    const [resolvedLogo, setResolvedLogo] = useState(null);
    const open = Boolean(anchorEl);
    const {
        queueNumber = "00",
        selectedOptions = [],
        ticketCode = "DEMO",
        isDemoMode = true,
        isLost = false,
    } = location.state || {};

    // Get ticket details from location state first (since cookie may be cleared), then cookie
    const ticketDataFromState = location.state?.ticketData;
    const ticketDetailsFromCookie = Cookies.get("ticketDetails");
    const ticketData = ticketDataFromState || (ticketDetailsFromCookie ? JSON.parse(ticketDetailsFromCookie) : null);
    const branchId = ticketData?.client?.id || resolvedBranchId;

    // Prefer the fetched name, falling back to whatever ticketData happened to carry.
    const resolvedBranchName = branchName || ticketData?.client?.name || "";
    const feedbackBox = FEEDBACK_BOXES.find((box) =>
        resolvedBranchName.toLowerCase().includes(box.branchMatch)
    );

    // Get logo filename from ticketData — if none uploaded, fall back to default logo
    const logoFilename = ticketData?.client?.logo || resolvedLogo;
    const logoUrl = logoFilename
        ? getStorageUrl(`/storage/logos/${logoFilename}`)
        : Logo;

    // Fetch fresh service settings (thank you texts) from API
    useEffect(() => {
        const fetchSettings = async () => {
            if (!branchId) return;
            try {
                const response = await getServiceSettings(branchId);
                if (response.success && response.data) {
                    if (response.data.thank_you_message) {
                        setThankYouMsg(response.data.thank_you_message);
                    }
                    if (response.data.thank_you_description) {
                        setThankYouDesc(response.data.thank_you_description);
                    }
                    if (response.data.thank_you_services_label) {
                        setThankYouServicesLabel(response.data.thank_you_services_label);
                    }
                }
            } catch (err) {
                console.error("Error fetching service settings for ThankYou:", err);
            }
        };
        fetchSettings();
    }, [branchId]);

    // Resolve the branch (name/logo/id) for branding and the feedback box.
    // By id when ticketData carried one, otherwise by the ticket code, which is
    // the branch's own code — the only identifier the SMS path passes through.
    const ticketDataBranchId = ticketData?.client?.id;
    useEffect(() => {
        let cancelled = false;

        const resolveBranch = async () => {
            try {
                if (ticketDataBranchId) {
                    const data = await getBranchDisplayInfo(ticketDataBranchId);
                    if (!cancelled && data?.branch_name) {
                        setBranchName(data.branch_name);
                    }
                    return;
                }

                // "DEMO"/"SMS" are placeholders the flows pass, not real branch codes.
                if (!ticketCode || ["DEMO", "SMS"].includes(ticketCode)) return;

                const data = await getBranchInfoByCode(ticketCode);
                if (cancelled || !data) return;
                if (data.branch_name) setBranchName(data.branch_name);
                if (data.branch_id) setResolvedBranchId(data.branch_id);
                if (data.logo) setResolvedLogo(data.logo);
            } catch (err) {
                console.error("Error resolving branch for ThankYou:", err);
            }
        };

        resolveBranch();
        return () => {
            cancelled = true;
        };
    }, [ticketDataBranchId, ticketCode]);

    // Preload logo for faster loading (only for remote URLs, not local imports)
    useEffect(() => {
        if (logoUrl && logoFilename && logoUrl.startsWith('http')) {
            try {
                // Create link element for preloading
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = logoUrl;
                link.fetchPriority = 'high';
                document.head.appendChild(link);

                // Also preconnect to the domain for faster connection
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = new URL(logoUrl).origin;
                document.head.appendChild(preconnect);

                return () => {
                    // Cleanup on unmount
                    if (document.head.contains(link)) {
                        document.head.removeChild(link);
                    }
                    if (document.head.contains(preconnect)) {
                        document.head.removeChild(preconnect);
                    }
                };
            } catch (error) {
                console.warn('Error preloading logo:', error);
            }
        }
    }, [logoUrl, logoFilename]);

    // Reset logo loaded state when logoUrl changes and add timeout fallback
    useEffect(() => {
        setLogoLoaded(false);
        
        // Fallback: if logo doesn't load within 5 seconds, show it anyway
        const timeout = setTimeout(() => {
            console.warn('Logo loading timeout, showing image anyway');
            setLogoLoaded(true);
        }, 5000);
        
        return () => clearTimeout(timeout);
    }, [logoUrl]);

    const handleBackToHome = () => {
        navigate("/getticket/" + ticketCode);
    };

    // Fix scrolling issue by overriding global overflow hidden and scroll to top
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        // Scroll to top immediately when component mounts
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (rootElement) {
            rootElement.scrollTop = 0;
        }
        
        // Also scroll to top after a small delay to ensure it works
        // This handles cases where content loads after initial render
        const scrollTimeout = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (rootElement) {
                rootElement.scrollTop = 0;
            }
        }, 100);
        
        // Also scroll after layout is complete
        const layoutTimeout = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (rootElement) {
                rootElement.scrollTop = 0;
            }
        }, 300);
        
        return () => {
            clearTimeout(scrollTimeout);
            clearTimeout(layoutTimeout);
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow || 'hidden';
            bodyElement.style.height = originalBodyHeight || '';
            htmlElement.style.overflow = originalHtmlOverflow || 'hidden';
            htmlElement.style.height = originalHtmlHeight || '';
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow || 'hidden';
                rootElement.style.height = originalRootHeight || '100%';
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    // Fetch menu items when branchId is available
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!branchId) {
                return;
            }

            try {
                const response = await getMenus(branchId);
                if (response.success) {
                    setMenuItems(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching menu items:", error);
                // Set empty array on error to prevent issues
                setMenuItems([]);
            }
        };

        fetchMenuItems();
    }, [branchId]);

    // Clear queue data when reaching thank you page
    useEffect(() => {
        const clearQueueData = () => {
            if (ticketCode && ticketCode !== "DEMO") {
                QueueManager.clearQueue(ticketCode);
                console.log("Queue data cleared after thank you page");
            }
        };

        clearQueueData();
    }, [ticketCode]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "24px",
                                    marginRight: "8px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {/* Default HOME menu item */}
                            <MenuItem onClick={handleClose}>
                                <a
                                    href={getUserFrontendUrl(`/get-ticket/${ticketData?.ticket_code || ticketCode || "DEMO"}`)}
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            {/* Dynamic menu items from database - only show if admin has set a real URL (not placeholder) */}
                            {menuItems
                                .filter((menu) => {
                                    if (!menu.menu_link || (!menu.menu_link.startsWith('http://') && !menu.menu_link.startsWith('https://'))) return false;
                                    const placeholders = [
                                        'https://www.facebook.com/yourpage',
                                        'https://www.instagram.com/yourprofile',
                                        'https://www.example.com/contact',
                                    ];
                                    return !placeholders.includes(menu.menu_link.trim().toLowerCase());
                                })
                                .map((menu) => (
                                    <MenuItem key={menu.id} onClick={() => {
                                        handleClose();
                                        window.open(menu.menu_link, '_blank', 'noopener,noreferrer');
                                    }}>
                                        {menu.menu_title}
                                    </MenuItem>
                                ))}
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 3 },
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                boxSizing: "border-box"
            }}
        >
            <Card
                sx={{
                    maxWidth: { xs: "100%", sm: 600 },
                    width: "100%",
                    textAlign: "center",
                    boxShadow: 3,
                    mb: { xs: 2, sm: 3 }
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box mb={{ xs: 1.5, sm: 2 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: isMobile ? 60 : 80, position: "relative" }}>
                        {!logoLoaded && (
                            <Skeleton 
                                variant="rectangular" 
                                width={isMobile ? 150 : 200} 
                                height={isMobile ? 60 : 80} 
                                sx={{ borderRadius: 1, position: "absolute" }}
                            />
                        )}
                        <img
                            src={logoUrl}
                            alt="FMB Logo"
                            loading="eager"
                            fetchPriority="high"
                            style={{
                                width: isMobile ? 150 : 200,
                                maxWidth: "100%",
                                height: "auto",
                                opacity: logoLoaded ? 1 : 0,
                                transition: "opacity 0.3s ease-in-out",
                                margin: "0 auto",
                                position: "relative"
                            }}
                            onLoad={() => {
                                console.log('Logo loaded successfully');
                                setLogoLoaded(true);
                            }}
                            onError={(e) => {
                                console.error('Logo failed to load:', logoUrl, e);
                                // Show fallback logo if error
                                if (logoUrl !== Logo) {
                                    e.target.src = Logo;
                                }
                                setLogoLoaded(true);
                            }}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mb={{ xs: 1, sm: 2 }}
                    >
                        {isLost ? (
                            <WarningIcon
                                sx={{
                                    fontSize: { xs: 48, sm: 64 },
                                    color: "#ff9800",
                                }}
                            />
                        ) : (
                            <CheckCircleIcon
                                sx={{
                                    fontSize: { xs: 48, sm: 64 },
                                    color: "#4caf50",
                                }}
                            />
                        )}
                    </Box>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ 
                            fontWeight: "bold",
                            color: isLost ? "#ff9800" : "#2e7d32",
                            fontSize: { xs: "1.4rem", sm: "1.75rem" },
                            px: { xs: 1, sm: 0 }
                        }}
                    >
                        {isLost
                            ? "Sorry, we could not find you when it was your turn"
                            : (thankYouMsg || ticketData?.client?.thank_you_message || "Thank you for your visit!")}
                    </Typography>

                    {isLost && (
                        <Typography
                            variant="body1"
                            sx={{ 
                                mb: { xs: 2, sm: 3 },
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                px: { xs: 1, sm: 0 },
                                lineHeight: { xs: 1.5, sm: 1.6 },
                                color: "#666",
                                fontStyle: "italic"
                            }}
                        >
                            We apologize that we could not reach you in the queue. Please take a new queue number if you still need help.
                        </Typography>
                    )}

                    {selectedOptions && selectedOptions.length > 0 && !isLost && (
                        <Box mb={{ xs: 2, sm: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ 
                                    fontWeight: "bold",
                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                    px: { xs: 1, sm: 0 }
                                }}
                            >
                                {thankYouServicesLabel || "Services you received help with:"}
                            </Typography>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="center"
                                gap={{ xs: 0.5, sm: 1 }}
                            >
                                {selectedOptions.map((option, index) => (
                                    <Typography
                                        key={index}
                                        variant="body1"
                                        sx={{
                                            backgroundColor: "#e8f5e8",
                                            color: "#2e7d32",
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 0.75, sm: 1 },
                                            borderRadius: 2,
                                            fontWeight: "medium",
                                            fontSize: { xs: "0.875rem", sm: "1rem" },
                                            mb: { xs: 0.5, sm: 0 }
                                        }}
                                    >
                                        {typeof option === "object"
                                            ? option.service_name
                                            : option}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {!isLost && (
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                mb: { xs: 0.5, sm: 1 },
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                px: { xs: 1, sm: 0 },
                                lineHeight: { xs: 1.5, sm: 1.6 }
                            }}
                        >
                            {thankYouDesc || ticketData?.client?.thank_you_description ||
                                "We hope you received good help and are satisfied with our service. Welcome back!"}
                        </Typography>
                    )}

                    {/* <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleBackToHome}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            textTransform: "none",
                        }}
                    >
                        Tilbake til start
                    </Button> */}
                </CardContent>
            </Card>

            {feedbackBox && !isLost && (
                <Card
                    sx={{
                        maxWidth: { xs: "100%", sm: 600 },
                        width: "100%",
                        textAlign: "center",
                        boxShadow: 3,
                        mb: { xs: 2, sm: 3 }
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                mb: 0.5
                            }}
                        >
                            {feedbackBox.subHeader}
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: "bold",
                                color: "#2e7d32",
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                mb: 1.5
                            }}
                        >
                            {feedbackBox.header}
                        </Typography>
                        <Typography
                            component="a"
                            href={feedbackBox.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: "inline-block",
                                color: "primary.main",
                                fontWeight: "medium",
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                                textDecoration: "underline"
                            }}
                        >
                            {(Array.isArray(feedbackBox.linkText)
                                ? feedbackBox.linkText
                                : [feedbackBox.linkText]
                            ).map((line) => (
                                <Box key={line} component="span" sx={{ display: "block" }}>
                                    {line}
                                </Box>
                            ))}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Box mt={{ xs: 2, sm: 3 }} textAlign="center" mb={2}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                    easyQue (c) by Digifront
                </Typography>
                <Typography 
                    variant="body2" 
                    color="primary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                    sales@digifront.biz
                </Typography>
            </Box>
        </Box>
        </>
    );
};

export default ThankYou;
